// src/admin/CatechistAttendanceManager.js
import React, { useState, useEffect, useContext, useMemo } from 'react';
import * as XLSX from 'xlsx';
import { StudentContext } from '../StudentContext';
import { firestore } from '../firebaseConfig';
import { collection, onSnapshot } from 'firebase/firestore';
import {
  fetchLeaveRequestsByBlock,
  fetchDateLocks,
  updateDateLockStatus,
  saveSingleCatechistAttendance,
  subscribeMeetingDates,
  updateMeetingDateStatus
} from '../FirebaseService';
import './CatechistAttendanceManager.css';

const getMandatorySundays = () => {
  const now = new Date();
  const dayOfWeek = now.getDay();
  const daysUntilSunday = (7 - dayOfWeek) % 7;

  const thisSunday = new Date(now);
  thisSunday.setDate(now.getDate() + daysUntilSunday);

  const lastSunday = new Date(thisSunday);
  lastSunday.setDate(thisSunday.getDate() - 7);

  const nextSunday = new Date(thisSunday);
  nextSunday.setDate(thisSunday.getDate() + 7);

  return {
    lastSundayStr: lastSunday.toISOString().split('T')[0],
    thisSundayStr: thisSunday.toISOString().split('T')[0],
    nextSundayStr: nextSunday.toISOString().split('T')[0]
  };
};

const CatechistAttendanceManager = () => {
  const { currentYearId, catechistsData, allBlocks } = useContext(StudentContext);

  const { lastSundayStr, thisSundayStr, nextSundayStr } = useMemo(() => getMandatorySundays(), []);
  
  const [availableDates, setAvailableDates] = useState([nextSundayStr, thisSundayStr, lastSundayStr]);
  const [selectedDate, setSelectedDate] = useState(thisSundayStr);

  const [meetingDatesMap, setMeetingDatesMap] = useState({});
  const [records, setRecords] = useState({});
  const [originalScanTimes, setOriginalScanTimes] = useState({});
  const [approvedLeavesMap, setApprovedLeavesMap] = useState({});
  
  // 🌟 Lưu toàn bộ dữ liệu điểm danh và đơn nghỉ phép phục vụ xuất Excel
  const [allAttendanceData, setAllAttendanceData] = useState({});
  const [allLeaveRequests, setAllLeaveRequests] = useState([]);

  const [dateLocks, setDateLocks] = useState({});
  const [isLockUpdating, setIsLockUpdating] = useState(false);

  const [searchTerm, setSearchTerm] = useState('');
  const [filterClass, setFilterClass] = useState('all');
  const [sortField, setSortField] = useState('fullName');
  const [sortDir, setSortDir] = useState('asc');
  const [columnFilters, setColumnFilters] = useState({
    mass: 'all',
    teaching: 'all',
    meeting: 'all'
  });

  const [loading, setLoading] = useState(true);
  const [timeModal, setTimeModal] = useState({ open: false, uid: '', field: '', time: '07:15' });

  const userRole = localStorage.getItem('userRole') || 'catechist';
  const blockLeader = localStorage.getItem('blockLeader') || '';
  const isAdmin = userRole === 'admin';

  const isCurrentDateLocked = Boolean(dateLocks[selectedDate]);
  const isEditable = isAdmin || !isCurrentDateLocked;

  // Lịch họp của ngày đang chọn
  const hasMeeting = Boolean(meetingDatesMap[selectedDate]);

  // Lắng nghe cấu hình ngày họp theo thời gian thực
  useEffect(() => {
    if (!currentYearId) return;
    const unsubscribe = subscribeMeetingDates(currentYearId, (datesMap) => {
      setMeetingDatesMap(datesMap || {});
    });
    return () => unsubscribe && unsubscribe();
  }, [currentYearId]);

  // Lọc Giáo lý viên trong Khối
  const blockCatechists = useMemo(() => {
    if (!catechistsData || catechistsData.length === 0) return [];

    return catechistsData.filter((c) => {
      if (c.status === 'inactive') return false;
      if (isAdmin) return true;

      if (!c.className && !c.blockLeader) return false;

      const userBlockClean = (blockLeader || '').toLowerCase().trim();
      const catechistBlockClean = (c.blockLeader || '').toLowerCase().trim();
      const catechistClassClean = (c.className || '').toLowerCase().trim();

      const matchesBlock = catechistBlockClean && (
        catechistBlockClean.includes(userBlockClean) || userBlockClean.includes(catechistBlockClean)
      );

      let matchesClassInBlock = false;
      if (allBlocks && Object.keys(allBlocks).length > 0) {
        Object.keys(allBlocks).forEach((groupCleanId) => {
          const classes = allBlocks[groupCleanId] || [];
          const groupName = classes[0]?.group || '';
          if (groupName.toLowerCase().includes(userBlockClean) || userBlockClean.includes(groupName.toLowerCase())) {
            if (classes.some(cls => cls.name.toLowerCase() === catechistClassClean || cls.id.toLowerCase() === catechistClassClean)) {
              matchesClassInBlock = true;
            }
          }
        });
      }

      return matchesBlock || matchesClassInBlock;
    });
  }, [catechistsData, blockLeader, isAdmin, allBlocks]);

  // Sắp xếp và lọc
  const sortedAndFilteredCatechists = useMemo(() => {
    let list = blockCatechists.filter((c) => {
      const matchClass = filterClass === 'all' || (c.className || '') === filterClass;
      const searchLower = searchTerm.toLowerCase();
      const matchSearch =
        (c.fullName && c.fullName.toLowerCase().includes(searchLower)) ||
        (c.holyName && c.holyName.toLowerCase().includes(searchLower)) ||
        (c.idCode && c.idCode.toLowerCase().includes(searchLower)) ||
        (c.username && c.username.toLowerCase().includes(searchLower));

      if (!matchClass || !matchSearch) return false;

      const uid = c.uid;
      const rec = records[uid] || { mass: 'Vắng không phép', teaching: 'Vắng không phép', meeting: 'Vắng không phép' };

      const getCategory = (val) => {
        if (!val) return 'unexcused';
        if (val.startsWith('Có lúc')) return 'present';
        if (val === 'Vắng có phép') return 'excused';
        return 'unexcused';
      };

      const matchMass = columnFilters.mass === 'all' || getCategory(rec.mass) === columnFilters.mass;
      const matchTeaching = columnFilters.teaching === 'all' || getCategory(rec.teaching) === columnFilters.teaching;
      const matchMeeting = columnFilters.meeting === 'all' || getCategory(rec.meeting) === columnFilters.meeting;

      return matchMass && matchTeaching && matchMeeting;
    });

    if (sortField) {
      list.sort((a, b) => {
        let valA = a[sortField] || '';
        let valB = b[sortField] || '';

        if (sortField === 'fullName') {
          const getLastName = (name) => {
            if (!name) return '';
            const parts = name.trim().split(/\s+/);
            return parts[parts.length - 1].toLowerCase();
          };
          valA = getLastName(a.fullName);
          valB = getLastName(b.fullName);
        } else {
          valA = valA.toString().toLowerCase();
          valB = valB.toString().toLowerCase();
        }

        if (valA < valB) return sortDir === 'asc' ? -1 : 1;
        if (valA > valB) return sortDir === 'asc' ? 1 : -1;
        return 0;
      });
    }

    return list;
  }, [blockCatechists, filterClass, searchTerm, columnFilters, records, sortField, sortDir]);

  const availableClasses = useMemo(() => {
    const setClasses = new Set();
    blockCatechists.forEach((c) => {
      if (c.className) setClasses.add(c.className);
    });
    return Array.from(setClasses).sort();
  }, [blockCatechists]);

  // Tải dữ liệu điểm danh thời gian thực
  useEffect(() => {
    if (!currentYearId || !selectedDate) return;
    setLoading(true);

    const targetBlock = isAdmin ? 'all' : blockLeader;

    Promise.all([
      fetchDateLocks(),
      fetchLeaveRequestsByBlock(currentYearId, targetBlock)
    ]).then(([locksData, leaveReqs]) => {
      setDateLocks(locksData || {});
      setAllLeaveRequests(leaveReqs || []);

      const leavesMap = {};
      (leaveReqs || []).forEach((req) => {
        if (req.status === 'approved' && req.requestDate === selectedDate) {
          leavesMap[req.catechistUid] = req;
        }
      });
      setApprovedLeavesMap(leavesMap);
    });

    const colRef = collection(firestore, `academic_years/${currentYearId}/catechist_attendances`);
    const unsubscribe = onSnapshot(colRef, (snapshot) => {
      const uniqueDataDates = new Set([nextSundayStr, thisSundayStr, lastSundayStr]);
      const savedAttMap = {};
      const allAttMap = {};

      snapshot.docs.forEach((d) => {
        const data = d.data();
        if (data) {
          allAttMap[d.id] = data;
          Object.keys(data).forEach((dateKey) => {
            if (dateKey.match(/^\d{4}-\d{2}-\d{2}$/)) {
              uniqueDataDates.add(dateKey);
            }
          });
          if (data[selectedDate]) {
            savedAttMap[d.id] = data[selectedDate];
          }
        }
      });

      setAllAttendanceData(allAttMap);
      setAvailableDates(Array.from(uniqueDataDates).sort().reverse());

      const initialRecords = {};
      const initialOriginalTimes = {};
      const defaultNowTime = new Date().toLocaleTimeString('vi-VN', { hour: '2-digit', minute: '2-digit', hour12: false });

      blockCatechists.forEach((c) => {
        const uid = c.uid;
        const saved = savedAttMap[uid] || {};
        const leave = approvedLeavesMap[uid];

        const defaultMass = leave?.absentActivities?.includes('mass') ? 'Vắng có phép' : 'Vắng không phép';
        const defaultTeaching = leave?.absentActivities?.includes('teaching') ? 'Vắng có phép' : 'Vắng không phép';
        const defaultMeeting = leave?.absentActivities?.includes('meeting') ? 'Vắng có phép' : 'Vắng không phép';

        const massVal = saved.mass || defaultMass;
        const teachingVal = saved.teaching || defaultTeaching;
        const meetingVal = saved.meeting || defaultMeeting;

        initialRecords[uid] = { mass: massVal, teaching: teachingVal, meeting: meetingVal };

        const extractTime = (val) => (val && val.startsWith('Có lúc ') ? val.replace('Có lúc ', '').trim() : defaultNowTime);
        initialOriginalTimes[uid] = {
          mass: extractTime(massVal),
          teaching: extractTime(teachingVal),
          meeting: extractTime(meetingVal)
        };
      });

      setRecords(initialRecords);
      setOriginalScanTimes(initialOriginalTimes);
      setLoading(false);
    }, (err) => {
      console.error('Lỗi realtime điểm danh:', err);
      setLoading(false);
    });

    return () => unsubscribe();
  }, [currentYearId, selectedDate, blockCatechists, blockLeader, isAdmin, lastSundayStr, thisSundayStr, nextSundayStr]);

  // Cập nhật trạng thái ngày họp vào Firestore khi click checkbox
  const handleToggleMeetingDate = async (e) => {
    if (!isEditable || !currentYearId) return;
    const nextVal = e.target.checked;
    await updateMeetingDateStatus(currentYearId, selectedDate, nextVal);
  };

  const handleToggleLockDate = async () => {
    if (!isAdmin || isLockUpdating) return;
    const nextLockStatus = !isCurrentDateLocked;
    setIsLockUpdating(true);

    try {
      const success = await updateDateLockStatus(selectedDate, nextLockStatus);
      if (success) {
        setDateLocks((prev) => ({ ...prev, [selectedDate]: nextLockStatus }));
        alert(`🎉 Đã ${nextLockStatus ? 'KHÓA' : 'MỞ KHÓA'} điểm danh ngày ${selectedDate} thành công!`);
      }
    } catch (err) {
      alert('❌ Lỗi hệ thống: ' + err.message);
    } finally {
      setIsLockUpdating(false);
    }
  };

  const handleSelectChange = async (uid, field, value) => {
    if (!isEditable) return;

    let finalValue = value;

    if (value === 'PRESENT_TIME') {
      const originalTime = originalScanTimes[uid]?.[field] || new Date().toLocaleTimeString('vi-VN', { hour: '2-digit', minute: '2-digit', hour12: false });
      finalValue = `Có lúc ${originalTime}`;
    } else if (value === 'CUSTOM_TIME' && isAdmin) {
      const originalTime = originalScanTimes[uid]?.[field] || '07:15';
      setTimeModal({ open: true, uid, field, time: originalTime });
      return;
    }

    const currentRecord = records[uid] || { mass: 'Vắng không phép', teaching: 'Vắng không phép', meeting: 'Vắng không phép' };
    const updatedRecord = { ...currentRecord, [field]: finalValue };

    await saveSingleCatechistAttendance(currentYearId, uid, selectedDate, updatedRecord);
  };

  const handleConfirmCustomTime = async () => {
    if (!timeModal.uid || !timeModal.field) return;
    const formattedTime = `Có lúc ${timeModal.time}`;

    const currentRecord = records[timeModal.uid] || { mass: 'Vắng không phép', teaching: 'Vắng không phép', meeting: 'Vắng không phép' };
    const updatedRecord = { ...currentRecord, [timeModal.field]: formattedTime };

    await saveSingleCatechistAttendance(currentYearId, timeModal.uid, selectedDate, updatedRecord);
    setTimeModal({ open: false, uid: '', field: '', time: '07:15' });
  };

  const handleHeaderSort = (field) => {
    if (sortField === field) {
      setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDir('asc');
    }
  };

  const getSundayNavbarLabel = (dStr) => {
    const [y, m, d] = dStr.split('-');
    const formatted = `${d}/${m}`;

    if (dStr === thisSundayStr) return `⭐ Chúa Nhật tới (${formatted})`;
    if (dStr === lastSundayStr) return `⏮️ Chúa Nhật tuần trước (${formatted})`;
    if (dStr === nextSundayStr) return `⏭️ Chúa Nhật tuần sau (${formatted})`;
    return `Chúa Nhật ${formatted}`;
  };

  const renderAttendanceSelect = (uid, field, currentValue) => {
    const isPresent = currentValue.startsWith('Có lúc');
    const originalTime = originalScanTimes[uid]?.[field] || '07:15';
    const presentLabel = isPresent ? `✓ ${currentValue}` : `✓ Có mặt (Có lúc ${originalTime})`;

    return (
      <select
        value={isPresent ? 'PRESENT_TIME' : currentValue}
        onChange={(e) => handleSelectChange(uid, field, e.target.value)}
        className={`cam-status-select ${getStatusClass(currentValue)}`}
        disabled={!isEditable}
      >
        <option value="PRESENT_TIME">{presentLabel}</option>
        {isAdmin && <option value="CUSTOM_TIME">⏱️ Sửa mốc giờ khác...</option>}
        <option value="Vắng có phép">🟡 Vắng có phép</option>
        <option value="Vắng không phép">🔴 Vắng không phép</option>
      </select>
    );
  };

  // Helper lấy thông tin điểm danh của 1 GLV tại một ngày cụ thể
  const getAttendanceRecord = (uid, dateStr) => {
    if (allAttendanceData[uid]?.[dateStr]) {
      return allAttendanceData[uid][dateStr];
    }
    if (allAttendanceData[dateStr]?.records?.[uid]) {
      return allAttendanceData[dateStr].records[uid];
    }
    return null;
  };

  // 🌟 HÀM XUẤT EXCEL TẤT CẢ CÁC NGÀY ĐÃ ĐIỂM DANH
  const handleExportExcel = () => {
    if (!sortedAndFilteredCatechists || sortedAndFilteredCatechists.length === 0) {
      alert('Không có dữ liệu Giáo lý viên để xuất file Excel!');
      return;
    }

    // 1. Tập hợp tất cả các ngày đã từng có dữ liệu điểm danh
    const attendedDatesSet = new Set();
    Object.entries(allAttendanceData).forEach(([docId, docData]) => {
      if (!docData) return;
      if (docId.match(/^\d{4}-\d{2}-\d{2}$/)) {
        if (docData.records && Object.keys(docData.records).length > 0) {
          attendedDatesSet.add(docId);
        }
      } else {
        Object.entries(docData).forEach(([key, val]) => {
          if (key.match(/^\d{4}-\d{2}-\d{2}$/) && val && typeof val === 'object') {
            if (val.mass || val.teaching || val.meeting) {
              attendedDatesSet.add(key);
            }
          }
        });
      }
    });

    let datesToExport = Array.from(attendedDatesSet).sort();
    if (datesToExport.length === 0) {
      datesToExport = [selectedDate];
    }

    // 2. Định dạng ngày hiển thị dd/mm/yyyy
    const formatHeaderDate = (dStr) => {
      if (!dStr) return '';
      const parts = dStr.split('-');
      if (parts.length !== 3) return dStr;
      return `${parts[2]}/${parts[1]}/${parts[0]}`;
    };

    // 3. Khởi tạo Header dòng 1 (Tên cột cố định & Cụm Ngày) và dòng 2 (Cột con)
    const headerRow1 = ['STT', 'Mã ID', 'Tên Thánh', 'Họ và Tên', 'Đứng Lớp'];
    const headerRow2 = ['', '', '', '', ''];

    // Cấu hình Merge 5 cột thông tin ban đầu (chiếm 2 dòng)
    const merges = [
      { s: { r: 0, c: 0 }, e: { r: 1, c: 0 } }, // STT
      { s: { r: 0, c: 1 }, e: { r: 1, c: 1 } }, // Mã ID
      { s: { r: 0, c: 2 }, e: { r: 1, c: 2 } }, // Tên Thánh
      { s: { r: 0, c: 3 }, e: { r: 1, c: 3 } }, // Họ và Tên
      { s: { r: 0, c: 4 }, e: { r: 1, c: 4 } }, // Đứng Lớp
    ];

    const colWidths = [
      { wch: 6 },
      { wch: 12 },
      { wch: 16 },
      { wch: 24 },
      { wch: 18 },
    ];

    let currentCol = 5;

    // Duyệt qua từng ngày để sinh cấu trúc 2 cột hoặc 3 cột
    datesToExport.forEach((dStr) => {
      const isMeeting = Boolean(meetingDatesMap[dStr]) ||
        Object.keys(allAttendanceData).some((uidKey) => {
          const rec = getAttendanceRecord(uidKey, dStr);
          return rec && Boolean(rec.meeting);
        });

      const dateLabel = `Ngày ${formatHeaderDate(dStr)}`;

      if (isMeeting) {
        // Ngày có họp: 3 cột con
        headerRow1.push(dateLabel, '', '');
        headerRow2.push('Đi Lễ', 'Dạy giáo lý', 'Họp');
        merges.push({ s: { r: 0, c: currentCol }, e: { r: 0, c: currentCol + 2 } });
        colWidths.push({ wch: 16 }, { wch: 16 }, { wch: 16 });
        currentCol += 3;
      } else {
        // Ngày không họp: 2 cột con
        headerRow1.push(dateLabel, '');
        headerRow2.push('Đi Lễ', 'Dạy giáo lý');
        merges.push({ s: { r: 0, c: currentCol }, e: { r: 0, c: currentCol + 1 } });
        colWidths.push({ wch: 16 }, { wch: 16 });
        currentCol += 2;
      }
    });

    // 4. Trích xuất dữ liệu của từng Giáo lý viên
    const dataRows = sortedAndFilteredCatechists.map((c, index) => {
      const row = [
        index + 1,
        c.idCode || '-',
        c.holyName || '',
        c.fullName || '',
        c.className || 'Chưa xếp lớp'
      ];

      datesToExport.forEach((dStr) => {
        const isMeeting = Boolean(meetingDatesMap[dStr]) ||
          Object.keys(allAttendanceData).some((uidKey) => {
            const r = getAttendanceRecord(uidKey, dStr);
            return r && Boolean(r.meeting);
          });

        const rec = getAttendanceRecord(c.uid, dStr);
        const leaveReq = allLeaveRequests.find(
          (req) => req.status === 'approved' && req.catechistUid === c.uid && req.requestDate === dStr
        );

        const defaultMass = leaveReq?.absentActivities?.includes('mass') ? 'Vắng có phép' : 'Vắng không phép';
        const defaultTeaching = leaveReq?.absentActivities?.includes('teaching') ? 'Vắng có phép' : 'Vắng không phép';
        const defaultMeeting = leaveReq?.absentActivities?.includes('meeting') ? 'Vắng có phép' : 'Vắng không phép';

        row.push(rec?.mass || defaultMass);
        row.push(rec?.teaching || defaultTeaching);

        if (isMeeting) {
          row.push(rec?.meeting || defaultMeeting);
        }
      });

      return row;
    });

    // 5. Khởi tạo Sheet và xuất file
    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.aoa_to_sheet([headerRow1, headerRow2, ...dataRows]);
    ws['!merges'] = merges;
    ws['!cols'] = colWidths;

    XLSX.utils.book_append_sheet(wb, ws, 'DiemDanhGLV');

    const cleanBlockName = blockLeader ? `_${blockLeader.replace(/\s+/g, '_')}` : '';
    const todayStr = new Date().toLocaleDateString('sv-SE');
    XLSX.writeFile(wb, `Diem_Danh_GLV${cleanBlockName}_${todayStr}.xlsx`);
  };

  return (
    <div className="cam-container">
      <div className="cam-header">
        <div>
          <h2 className="cam-title">
            ⛪ Điểm Danh Giáo Lý Viên
            {blockLeader && !isAdmin && <span className="cam-block-tag">Khối: {blockLeader}</span>}
          </h2>
          <p className="cam-subtitle">
            {isAdmin ? 'Quyền Admin: Quản lý & chỉnh sửa toàn bộ Khối.' : 'Quyền Trưởng Khối: Điểm danh thời gian thực cho GLV trong Khối.'}
          </p>
        </div>

        <div className="cam-actions-top">
          {/* 🌟 Nút Tải File Excel */}
          <button
            onClick={handleExportExcel}
            className="cam-btn cam-btn-excel"
            title="Xuất file Excel điểm danh tất cả các ngày"
          >
            📥 Xuất File Excel
          </button>

          {isAdmin && (
            <button
              onClick={handleToggleLockDate}
              disabled={isLockUpdating || loading}
              className={`cam-btn ${isCurrentDateLocked ? 'cam-btn-unlock' : 'cam-btn-lock'}`}
            >
              {isCurrentDateLocked ? '🔓 Mở Khóa Ngày Này' : '🔒 Khóa Ngày Này'}
            </button>
          )}
        </div>
      </div>

      {isCurrentDateLocked && (
        <div className="cam-lock-banner">
          🔒 <strong>NGÀY NÀY ĐÃ BỊ KHÓA:</strong> Trưởng khối chỉ có thể xem dữ liệu.
          {isAdmin && ' (Bạn là Admin nên vẫn có thể chỉnh sửa & mở khóa).'}
        </div>
      )}

      {/* NAVBAR CHỌN NGÀY CHÚA NHẬT */}
      <div className="cam-date-navbar-wrapper">
        <span className="cam-navbar-label">📅 CHỌN NGÀY:</span>
        <nav className="cam-date-navbar">
          {availableDates.map((dStr) => (
            <button
              key={dStr}
              onClick={() => setSelectedDate(dStr)}
              className={`cam-date-item ${selectedDate === dStr ? 'active' : ''} ${dateLocks[dStr] ? 'locked' : ''}`}
            >
              {getSundayNavbarLabel(dStr)}
              {dateLocks[dStr] && <span className="cam-lock-icon">🔒</span>}
            </button>
          ))}
        </nav>
      </div>

      {/* BỘ LỌC VÀ TÌM KIẾM */}
      <div className="cam-controls-card">
        <div className="cam-control-group">
          <label className="cam-toggle-label">
            <input
              type="checkbox"
              checked={hasMeeting}
              onChange={handleToggleMeetingDate}
              disabled={!isEditable}
              className="cam-checkbox"
            />
            <span>👥 Hôm nay có lịch Họp GLV</span>
          </label>
        </div>

        <div className="cam-control-group">
          <select
            value={filterClass}
            onChange={(e) => setFilterClass(e.target.value)}
            className="cam-select-filter"
          >
            <option value="all">-- Tất cả Lớp --</option>
            {availableClasses.map((cls) => (
              <option key={cls} value={cls}>{cls}</option>
            ))}
          </select>

          <input
            type="text"
            placeholder="Tìm tên GLV, ID Code..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="cam-search-input"
          />
        </div>
      </div>

      {/* BẢNG ĐIỂM DANH */}
      <div className="cam-table-wrapper">
        <table className="cam-table">
          <thead>
            <tr>
              <th className="cam-th cam-text-center">STT</th>
              
              <th className="cam-th cam-sortable" onClick={() => handleHeaderSort('idCode')}>
                ID Code {sortField === 'idCode' && (sortDir === 'asc' ? '↑' : '↓')}
              </th>

              <th className="cam-th cam-sticky-head cam-sortable" onClick={() => handleHeaderSort('fullName')}>
                Tên Thánh & Họ Tên {sortField === 'fullName' && (sortDir === 'asc' ? '↑' : '↓')}
              </th>

              <th className="cam-th cam-sortable" onClick={() => handleHeaderSort('className')}>
                Đứng Lớp {sortField === 'className' && (sortDir === 'asc' ? '↑' : '↓')}
              </th>

              <th className="cam-th cam-th-center">
                <div>⛪ Đi Lễ Chúa Nhật</div>
                <select
                  value={columnFilters.mass}
                  onChange={(e) => setColumnFilters({ ...columnFilters, mass: e.target.value })}
                  className="cam-th-filter-select"
                >
                  <option value="all">Tất cả</option>
                  <option value="present">Có mặt</option>
                  <option value="excused">Vắng có phép</option>
                  <option value="unexcused">Vắng không phép</option>
                </select>
              </th>

              <th className="cam-th cam-th-center">
                <div>📖 Dạy Giáo Lý</div>
                <select
                  value={columnFilters.teaching}
                  onChange={(e) => setColumnFilters({ ...columnFilters, teaching: e.target.value })}
                  className="cam-th-filter-select"
                >
                  <option value="all">Tất cả</option>
                  <option value="present">Có mặt</option>
                  <option value="excused">Vắng có phép</option>
                  <option value="unexcused">Vắng không phép</option>
                </select>
              </th>

              {hasMeeting && (
                <th className="cam-th cam-th-center highlight-meeting">
                  <div>👥 Đi Họp GLV</div>
                  <select
                    value={columnFilters.meeting}
                    onChange={(e) => setColumnFilters({ ...columnFilters, meeting: e.target.value })}
                    className="cam-th-filter-select"
                  >
                    <option value="all">Tất cả</option>
                    <option value="present">Có mặt</option>
                    <option value="excused">Vắng có phép</option>
                    <option value="unexcused">Vắng không phép</option>
                  </select>
                </th>
              )}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={hasMeeting ? 7 : 6} className="cam-td-empty">
                  ⏳ Đang đồng bộ dữ liệu thời gian thực...
                </td>
              </tr>
            ) : sortedAndFilteredCatechists.length === 0 ? (
              <tr>
                <td colSpan={hasMeeting ? 7 : 6} className="cam-td-empty">
                  Không tìm thấy Giáo lý viên nào phù hợp với bộ lọc.
                </td>
              </tr>
            ) : (
              sortedAndFilteredCatechists.map((c, index) => {
                const uid = c.uid;
                const rec = records[uid] || { mass: 'Vắng không phép', teaching: 'Vắng không phép', meeting: 'Vắng không phép' };
                const leaveReq = approvedLeavesMap[uid];

                return (
                  <tr key={uid}>
                    <td className="cam-td cam-text-center">{index + 1}</td>
                    <td className="cam-td cam-id-code">{c.idCode || '-'}</td>

                    <td className="cam-td cam-sticky-col">
                      <div>
                        <span className="cam-holy-name">{c.holyName}</span>
                        <strong className="cam-full-name">{c.fullName}</strong>
                      </div>
                      {leaveReq && (
                        <span className="cam-leave-badge" title={`Lý do: ${leaveReq.reason}`}>
                          📄 Có đơn xin nghỉ
                        </span>
                      )}
                    </td>

                    <td className="cam-td">{c.className || 'Chưa xếp lớp'}</td>

                    <td className="cam-td cam-text-center">
                      {renderAttendanceSelect(uid, 'mass', rec.mass)}
                    </td>

                    <td className="cam-td cam-text-center">
                      {renderAttendanceSelect(uid, 'teaching', rec.teaching)}
                    </td>

                    {hasMeeting && (
                      <td className="cam-td cam-text-center highlight-meeting-cell">
                        {renderAttendanceSelect(uid, 'meeting', rec.meeting)}
                      </td>
                    )}
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {timeModal.open && isAdmin && (
        <div className="cam-modal-overlay">
          <div className="cam-modal-card">
            <h3>⏱️ Nhập Thời Gian Có Mặt (Admin)</h3>
            <div className="cam-form-group">
              <label>Thời gian xuất hiện (HH:mm):</label>
              <input
                type="time"
                value={timeModal.time}
                onChange={(e) => setTimeModal({ ...timeModal, time: e.target.value })}
                className="cam-time-input"
              />
            </div>
            <div className="cam-modal-actions">
              <button
                type="button"
                onClick={() => setTimeModal({ open: false, uid: '', field: '', time: '07:15' })}
                className="cam-btn-cancel"
              >
                Hủy
              </button>
              <button
                type="button"
                onClick={handleConfirmCustomTime}
                className="cam-btn-confirm"
              >
                Xác Nhận & Lưu
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const getStatusClass = (status) => {
  if (status.startsWith('Có lúc')) return 'status-present';
  if (status === 'Vắng có phép') return 'status-excused';
  return 'status-unexcused';
};

export default CatechistAttendanceManager;